import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { SimpleFieldset } from "../../components/simplePage/simpleFieldset.tsx";
import { servicePost } from "../../api/client.ts";
import { useAuth } from "../../auth/authContext.tsx";
import styles from "../../styles/formPage.module.scss";
import type { ProductEditorHandle, ProductEditorProps } from "./editProductShared.ts";

/* ── Types ──────────────────────────────────────────────── */

interface MovieProduct {
  ProductID: number;
  OperatorID: number;
  Title: string;
  OriginalTitle: string;
  Format: string;
  StreetDate: string;
  EndDate: string;
  ImageUrl: string;
  AdditionalImageUrl: string;
  Company: string;
  Deal: string;
  RegionCode: string;
  EAN: string;
  UPC: string;
  BuyPrice: number;
  BuyTax: number;
  RentalPrice: number;
  RentalTax: number;
  Rating: string;
  Persons: string;
  Genres: string;
  Sounds: string;
  Subtitles: string;
  Languages: string;
  Screens: string;
  ParentProductID: number;
}

const FORMAT_OPTIONS = [
  { value: "", label: "Select..." },
  { value: "DVD", label: "DVD" },
  { value: "Blu-ray", label: "Blu-ray" },
  { value: "4K", label: "4K UHD" },
  { value: "iGame", label: "iGame" },
  { value: "CD", label: "CD" },
];

/* ── Component ──────────────────────────────────────────── */

export const EditProductMovie = forwardRef<ProductEditorHandle, ProductEditorProps>(
  function EditProductMovie({ productId, onSaved, onStateChange }, ref) {
  const [product, setProduct] = useState<MovieProduct>(emptyProduct());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [dirty, setDirty] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const isNew = !productId;
  const isGame = product.Format === "iGame";
  const { upsertProduct } = useAuth();

  useEffect(() => {
    onStateChange?.({ saving, status, dirty });
  }, [saving, status, dirty, onStateChange]);

  useEffect(() => {
    if (productId <= 0) {
      setProduct(emptyProduct());
      setDirty(false);
      return;
    }
    setLoading(true);
    servicePost<MovieProduct>("GetProduct", { id: productId })
      .then(result => {
        if (!result.bError && result.oData) {
          setProduct(result.oData);
          setDirty(false);
        } else {
          setStatus({ type: "error", text: "Failed to load product" });
        }
      })
      .finally(() => setLoading(false));
  }, [productId]);

  const handleChange = useCallback((field: keyof MovieProduct, value: string | number) => {
    setProduct(prev => ({ ...prev, [field]: value }));
    setDirty(true);
  }, []);

  const toggle = useCallback((section: string) => {
    setCollapsed(prev => ({ ...prev, [section]: !prev[section] }));
  }, []);

  const handleSave = useCallback(async () => {
    if (!product.Title.trim()) {
      setStatus({ type: "error", text: "Product title is required." });
      return;
    }
    setSaving(true);
    setStatus(null);
    try {
      const result = await servicePost<MovieProduct>("SaveDVDProduct", {
        product,
        person: product.Persons,
        genre: product.Genres,
        sound: product.Sounds,
        subtitle: product.Subtitles,
        language: product.Languages,
        screen: product.Screens,
      });
      if (result.bError || !result.oData) {
        setStatus({ type: "error", text: result.sMessage ?? "Failed to save product" });
      } else {
        // Keep the price the user just submitted — the save response must not
        // blank the price fields (price is stored separately in S_Generic_Price),
        // or a later save would resubmit 0 and wipe it.
        setProduct(prev => ({
          ...result.oData!,
          BuyPrice: prev.BuyPrice, BuyTax: prev.BuyTax,
          RentalPrice: prev.RentalPrice, RentalTax: prev.RentalTax,
        }));
        setDirty(false);
        upsertProduct({
          ProductID: result.oData.ProductID,
          Name: result.oData.Title,
          Deleted: false,
        });
        setStatus({ type: "success", text: isNew ? "Product Created" : "Product Updated" });
        onSaved();
      }
    } finally {
      setSaving(false);
    }
  }, [product, isNew, onSaved, upsertProduct]);

  useImperativeHandle(ref, () => ({ save: handleSave }), [handleSave]);

  if (loading) return <p>Loading...</p>;

  return (
    <>
      {/* ── Product ───────────────────────────────── */}
      <AccordionSection title="Product" collapsed={collapsed["product"]} onToggle={() => toggle("product")}>
        <div className={styles.fieldRowStacked}>
          <label>Title</label>
          <input className={styles.fieldInput} value={product.Title} onChange={e => handleChange("Title", e.target.value)} />
        </div>
        {!isGame && (
          <div className={styles.fieldRowStacked}>
            <label>Original Title</label>
            <input className={styles.fieldInput} value={product.OriginalTitle} onChange={e => handleChange("OriginalTitle", e.target.value)} />
          </div>
        )}
        <div className={styles.fieldRowStacked}>
          <label>Format</label>
          <select className={styles.fieldSelect} value={product.Format} onChange={e => handleChange("Format", e.target.value)}>
            {FORMAT_OPTIONS.map(f => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </div>
        <div style={{ display: "flex", gap: "var(--space-3)" }}>
          <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
            <label>Street Date</label>
            <input className={styles.fieldInput} type="date" value={product.StreetDate} onChange={e => handleChange("StreetDate", e.target.value)} />
          </div>
          {!isGame && (
            <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
              <label>End Date</label>
              <input className={styles.fieldInput} type="date" value={product.EndDate} onChange={e => handleChange("EndDate", e.target.value)} />
            </div>
          )}
        </div>
        <div className={styles.fieldRowStacked}>
          <label>Parent Product ID</label>
          <input className={styles.fieldInput} type="number" value={product.ParentProductID || ""} onChange={e => handleChange("ParentProductID", parseInt(e.target.value) || 0)} />
        </div>
      </AccordionSection>

      {/* ── Product Image ─────────────────────────── */}
      <AccordionSection title="Product Image" collapsed={collapsed["image"]} onToggle={() => toggle("image")}>
        <div className={styles.fieldRowStacked}>
          <label>Image</label>
          <input className={styles.fieldInput} value={product.ImageUrl} readOnly />
          {product.ImageUrl && (
            <div style={{ textAlign: "center", padding: "var(--space-2)" }}>
              <img src={product.ImageUrl} alt={product.Title} style={{ maxHeight: 200, maxWidth: 300, border: "1px solid var(--color-border-default)" }} />
            </div>
          )}
        </div>
        {isNew && <p className={styles.hint} style={{ color: "var(--color-error)" }}>Create the product before uploading images.</p>}
      </AccordionSection>

      {/* ── Additional Image ──────────────────────── */}
      {!isGame && (
        <AccordionSection title="Additional Product Image" collapsed={collapsed["addImage"]} onToggle={() => toggle("addImage")}>
          <div className={styles.fieldRowStacked}>
            <label>Additional Image</label>
            <input className={styles.fieldInput} value={product.AdditionalImageUrl} readOnly />
            {product.AdditionalImageUrl && (
              <div style={{ textAlign: "center", padding: "var(--space-2)" }}>
                <img src={product.AdditionalImageUrl} alt="Additional" style={{ maxHeight: 200, maxWidth: 300, border: "1px solid var(--color-border-default)" }} />
              </div>
            )}
          </div>
        </AccordionSection>
      )}

      {/* ── Details ───────────────────────────────── */}
      {!isGame && (
        <AccordionSection title="Details" collapsed={collapsed["details"]} onToggle={() => toggle("details")}>
          <div style={{ display: "flex", gap: "var(--space-3)" }}>
            <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
              <label>Company</label>
              <input className={styles.fieldInput} value={product.Company} onChange={e => handleChange("Company", e.target.value)} />
            </div>
            <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
              <label>Deal</label>
              <input className={styles.fieldInput} value={product.Deal} onChange={e => handleChange("Deal", e.target.value)} />
            </div>
          </div>
          <div style={{ display: "flex", gap: "var(--space-3)" }}>
            <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
              <label>Region Code</label>
              <input className={styles.fieldInput} value={product.RegionCode} onChange={e => handleChange("RegionCode", e.target.value)} />
            </div>
            <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
              <label>EAN</label>
              <input className={styles.fieldInput} value={product.EAN} onChange={e => handleChange("EAN", e.target.value)} />
            </div>
            <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
              <label>UPC</label>
              <input className={styles.fieldInput} value={product.UPC} onChange={e => handleChange("UPC", e.target.value)} />
            </div>
          </div>
        </AccordionSection>
      )}

      {/* ── Pricing ───────────────────────────────── */}
      <AccordionSection title="Pricing" collapsed={collapsed["pricing"]} onToggle={() => toggle("pricing")}>
        <div style={{ display: "flex", gap: "var(--space-3)" }}>
          <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
            <label>Buy Price</label>
            <input className={styles.fieldInput} type="number" step="0.01" min="0" value={product.BuyPrice} onChange={e => handleChange("BuyPrice", Math.max(0, parseFloat(e.target.value) || 0))} />
          </div>
          <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
            <label>Buy Tax</label>
            <input className={styles.fieldInput} type="number" step="0.01" min="0" value={product.BuyTax} onChange={e => handleChange("BuyTax", Math.max(0, parseFloat(e.target.value) || 0))} />
          </div>
        </div>
        <div style={{ display: "flex", gap: "var(--space-3)" }}>
          <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
            <label>Rental Price</label>
            <input className={styles.fieldInput} type="number" step="0.01" min="0" value={product.RentalPrice} onChange={e => handleChange("RentalPrice", Math.max(0, parseFloat(e.target.value) || 0))} />
          </div>
          <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
            <label>Rental Tax</label>
            <input className={styles.fieldInput} type="number" step="0.01" min="0" value={product.RentalTax} onChange={e => handleChange("RentalTax", Math.max(0, parseFloat(e.target.value) || 0))} />
          </div>
        </div>
      </AccordionSection>

      {/* ── Rating ────────────────────────────────── */}
      {!isGame && (
        <AccordionSection title="Rating" collapsed={collapsed["rating"]} onToggle={() => toggle("rating")}>
          <div className={styles.fieldRowStacked}>
            <label>Rating</label>
            <input className={styles.fieldInput} value={product.Rating} onChange={e => handleChange("Rating", e.target.value)} />
          </div>
        </AccordionSection>
      )}

      {/* ── Attributes ────────────────────────────── */}
      {!isGame && (
        <AccordionSection title="Attributes" collapsed={collapsed["attributes"]} onToggle={() => toggle("attributes")}>
          <div className={styles.fieldRowStacked}>
            <label>Persons (comma-separated)</label>
            <textarea className={styles.fieldTextarea} value={product.Persons} onChange={e => handleChange("Persons", e.target.value)} rows={2} />
          </div>
          <div className={styles.fieldRowStacked}>
            <label>Genres (comma-separated)</label>
            <input className={styles.fieldInput} value={product.Genres} onChange={e => handleChange("Genres", e.target.value)} />
          </div>
          <div style={{ display: "flex", gap: "var(--space-3)" }}>
            <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
              <label>Sound</label>
              <input className={styles.fieldInput} value={product.Sounds} onChange={e => handleChange("Sounds", e.target.value)} />
            </div>
            <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
              <label>Subtitles</label>
              <input className={styles.fieldInput} value={product.Subtitles} onChange={e => handleChange("Subtitles", e.target.value)} />
            </div>
          </div>
          <div style={{ display: "flex", gap: "var(--space-3)" }}>
            <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
              <label>Languages</label>
              <input className={styles.fieldInput} value={product.Languages} onChange={e => handleChange("Languages", e.target.value)} />
            </div>
            <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
              <label>Screens</label>
              <input className={styles.fieldInput} value={product.Screens} onChange={e => handleChange("Screens", e.target.value)} />
            </div>
          </div>
        </AccordionSection>
      )}

    </>
  );
},
);

/* ── AccordionSection ──────────────────────────────────── */

function AccordionSection({ title, collapsed, onToggle, children }: {
  title: string;
  collapsed?: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <SimpleFieldset legend="">
      <button
        type="button"
        onClick={onToggle}
        style={{
          display: "flex", alignItems: "center", gap: "var(--space-1)",
          width: "100%", background: "none", border: "none", cursor: "pointer",
          fontSize: "var(--text-sm)", fontWeight: "var(--font-semibold)" as never,
          color: "var(--color-text)", padding: "var(--space-1) 0",
        }}
      >
        <span style={{ transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform 150ms", display: "inline-block" }}>▾</span>
        {title}
      </button>
      {!collapsed && <div style={{ paddingTop: "var(--space-2)" }}>{children}</div>}
    </SimpleFieldset>
  );
}

/* ── Helpers ───────────────────────────────────────────── */

function emptyProduct(): MovieProduct {
  return {
    ProductID: 0, OperatorID: 0, Title: "", OriginalTitle: "",
    Format: "", StreetDate: "", EndDate: "", ImageUrl: "",
    AdditionalImageUrl: "", Company: "", Deal: "", RegionCode: "",
    EAN: "", UPC: "", BuyPrice: 0, BuyTax: 0,
    RentalPrice: 0, RentalTax: 0, Rating: "",
    Persons: "", Genres: "", Sounds: "", Subtitles: "",
    Languages: "", Screens: "", ParentProductID: 0,
  };
}
